import { useEffect, useState } from "react";
import { useProductAdvisor } from "./ProductAdvisorContext";

export default function FloatingAdvisorButton() {
  const { open, startAutoPrompt, dismissAutoPrompt } = useProductAdvisor();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!startAutoPrompt) return;
    const timer = setTimeout(() => {
      setShowTooltip(true);
    }, 6500);
    return () => clearTimeout(timer);
  }, [startAutoPrompt]);

  const handleOpen = () => {
    setShowTooltip(false);
    open();
  };

  const handleDismissTooltip = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(false);
    dismissAutoPrompt();
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {/* Auto prompt tooltip */}
      {showTooltip && (
        <div className="relative max-w-xs animate-fade-in">
          <div className="glass-card px-4 py-3 rounded-xl text-left">
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20 flex-shrink-0 mt-0.5">
                <i className="ri-compass-3-line text-emerald-accent text-xs" />
              </div>
              <div>
                <p className="text-xs text-silver-200 leading-relaxed mb-1">
                  Not sure which solution fits your business? I can help you find the right one.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleOpen}
                    className="text-xs font-semibold text-emerald-accent hover:text-emerald-soft transition-colors cursor-pointer"
                  >
                    Get started
                  </button>
                  <button
                    onClick={handleDismissTooltip}
                    className="text-xs text-silver-500 hover:text-silver-300 transition-colors cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Triangle */}
          <div className="absolute -bottom-1.5 right-5 w-3 h-3 bg-graphite-800/60 rotate-45 border-r border-b border-graphite-700/50" />
        </div>
      )}

      {/* Floating button with intense emerald glow + pulse rings */}
      <div className="relative">
        {/* Glowing outer light */}
        <span
          className="absolute -inset-1 rounded-xl bg-emerald-accent/20 blur-md"
          style={{ animation: "shimmerBorder 2.2s ease-in-out infinite" }}
        />

        {/* Outer pulse rings */}
        <span
          className="absolute inset-0 rounded-xl border-2 border-emerald-accent/50"
          style={{ animation: "ringPulse 2.2s ease-out infinite" }}
        />
        <span
          className="absolute inset-0 rounded-xl border-2 border-emerald-accent/35"
          style={{ animation: "ringPulseDelay 2.2s ease-out 0.73s infinite" }}
        />
        <span
          className="absolute inset-0 rounded-xl border border-emerald-accent/20"
          style={{ animation: "ringPulseThird 2.2s ease-out 1.47s infinite" }}
        />

        <button
          onClick={handleOpen}
          className="relative flex items-center gap-2.5 px-4 py-3 bg-emerald-accent text-white font-semibold text-sm rounded-xl hover:bg-emerald-hover transition-all duration-300 cursor-pointer"
          style={{ animation: "shimmerBorder 2.2s ease-in-out infinite, floatUpDown 3.5s ease-in-out infinite" }}
          aria-label="Open Product Advisor"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-compass-3-line text-base" />
          </span>
          <span className="hidden sm:inline">Product Advisor</span>
        </button>
      </div>
    </div>
  );
}
